import React, { useCallback, useEffect, useState } from 'react';
import { Typography, Spin } from 'antd';
import { CalendarOutlined, WarningOutlined, CloseOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { clientSlotsApi } from '../../../api/clientSlotsApi';
import { useBookingFlowStore } from '../../../store/bookingFlowStore';
import { useMainButton } from '../../../hooks/useMainButton';
import { DateCarousel } from '../ui/DateCarousel';
import { SlotGrid } from '../ui/SlotGrid';
import { ErrorRetry } from '../ui/ErrorRetry';

interface Props {
  onNext: () => void;
  conflictError?: boolean;
  onConflictAck?: () => void;
}

function todayISO(): string {
  const d = new Date();
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

export const TimeStep: React.FC<Props> = ({ onNext, conflictError, onConflictAck }) => {
  const { t } = useTranslation('customer');
  const providerId = useBookingFlowStore((s) => s.providerId);
  const serviceId = useBookingFlowStore((s) => s.serviceId);
  const selectedDate = useBookingFlowStore((s) => s.selectedDate);
  const selectedTime = useBookingFlowStore((s) => s.selectedTime);
  const setDateTime = useBookingFlowStore((s) => s.setDateTime);

  const [slots, setSlots] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [nextLoading, setNextLoading] = useState(false);
  const [noSlots, setNoSlots] = useState(false);

  const activeDate = selectedDate ?? todayISO();

  const fetchSlots = useCallback(async (d: string) => {
    if (providerId === null || serviceId === null) return;
    setLoading(true);
    setError(null);
    setNoSlots(false);
    try {
      const data = await clientSlotsApi.getSlots({ providerId, serviceId, date: d });
      setSlots(data);
      setNoSlots(data.length === 0);
    } catch {
      setSlots([]);
      setNoSlots(false);
      setError(t('bookingFlow.time.loadError'));
    } finally {
      setLoading(false);
    }
  }, [providerId, serviceId, t]);

  useEffect(() => { void fetchSlots(activeDate); }, [activeDate, fetchSlots]);

  const handleDateChange = useCallback((d: string) => {
    setDateTime(d, '');
  }, [setDateTime]);

  const handleSelectSlot = useCallback((time: string) => {
    setDateTime(activeDate, time);
  }, [setDateTime, activeDate]);

  const handleNextAvailable = useCallback(async () => {
    if (providerId === null || serviceId === null) return;
    setNextLoading(true);
    try {
      const next = await clientSlotsApi.getNextAvailable({ providerId, serviceId });
      if (next !== null) setDateTime(next.date, '');
    } catch { /* ignore */ } finally {
      setNextLoading(false);
    }
  }, [providerId, serviceId, setDateTime]);

  useMainButton(t('bookingFlow.nextBtn'), onNext, selectedTime !== null && selectedTime !== '');

  return (
    <div>
      {/* Title */}
      <div style={{ padding: '14px 16px 4px' }}>
        <Typography.Text style={{ fontSize: 17, fontWeight: 600, color: 'var(--bl-text)', letterSpacing: -0.3 }}>
          {t('bookingFlow.time.title')}
        </Typography.Text>
      </div>

      {/* Conflict error */}
      {conflictError === true && (
        <div style={{
          margin: '8px 16px',
          borderRadius: 12,
          background: 'var(--bl-surface)',
          border: '1px solid rgba(239,68,68,0.25)',
          padding: '10px 14px',
          display: 'flex', alignItems: 'center', gap: 10,
        }}>
          <div style={{
            width: 32, height: 32, borderRadius: 'var(--bl-radius-sm)', flexShrink: 0,
            background: 'rgba(239,68,68,0.10)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 15, color: 'rgba(239,68,68,1)',
          }}>
            <WarningOutlined />
          </div>
          <Typography.Text style={{ fontSize: 13, fontWeight: 500, color: 'rgba(220,38,38,1)', flex: 1 }}>
            {t('bookingFlow.time.conflict')}
          </Typography.Text>
          <button
            type="button"
            onClick={onConflictAck}
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--bl-text-secondary)', padding: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', minWidth: 44, minHeight: 44 }}
          >
            <CloseOutlined style={{ fontSize: 14 }} />
          </button>
        </div>
      )}

      <DateCarousel selectedDate={activeDate} onChange={handleDateChange} />

      {loading && (
        <div style={{ display: 'flex', justifyContent: 'center', padding: 32 }}>
          <Spin size="large" />
        </div>
      )}

      {!loading && error !== null && (
        <ErrorRetry message={error} onRetry={() => void fetchSlots(activeDate)} />
      )}

      {!loading && error === null && noSlots && (
        <div style={{
          margin: '8px 16px',
          borderRadius: 'var(--bl-radius-lg)',
          background: 'var(--bl-surface)',
          border: '1px solid var(--bl-divider)',
          padding: '24px 16px',
          textAlign: 'center',
        }}>
          <div style={{
            width: 48, height: 48, borderRadius: 'var(--bl-radius-sm)',
            background: 'rgba(51,144,236,0.10)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 22, color: 'var(--bl-primary)',
            margin: '0 auto 12px',
          }}>
            <CalendarOutlined />
          </div>
          <Typography.Text style={{ display: 'block', fontSize: 15, fontWeight: 600, color: 'var(--bl-text)', marginBottom: 4 }}>
            {t('bookingFlow.time.noSlotsTitle')}
          </Typography.Text>
          <Typography.Text style={{ display: 'block', fontSize: 13, color: 'var(--bl-text-secondary)', marginBottom: 16 }}>
            {t('bookingFlow.time.noSlotsHint')}
          </Typography.Text>
          <button
            type="button"
            onClick={() => void handleNextAvailable()}
            disabled={nextLoading}
            style={{
              minHeight: 44, borderRadius: 'var(--bl-radius)',
              background: 'rgba(51,144,236,0.10)',
              color: 'var(--bl-primary)',
              border: 'none', cursor: nextLoading ? 'not-allowed' : 'pointer',
              fontSize: 13, fontWeight: 600,
              fontFamily: 'inherit',
              padding: '0 20px',
              opacity: nextLoading ? 0.6 : 1,
            }}
          >
            {nextLoading ? t('bookingFlow.time.searching') : t('bookingFlow.time.nextAvailable')}
          </button>
        </div>
      )}

      {!loading && error === null && !noSlots && slots.length > 0 && (
        <SlotGrid
          slots={slots}
          selectedTime={selectedTime !== null && selectedTime !== '' ? selectedTime : null}
          onSelect={handleSelectSlot}
        />
      )}
    </div>
  );
};
